import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  CalendarItems,
  checkIn,
  CheckInParams,
  createInvitations,
  getAllTodaysCalendarItems,
  getCalendarItem,
  getCalendarItems,
  getSinglePageCalendarItems,
  revokeInvitation,
} from "./calendarApi";
import { useContext } from "react";
import { AppContext } from "../context/app";
import { format } from "date-fns";

export function useCheckinMutation(
  options?: UseMutationOptions<
    void,
    unknown,
    Omit<CheckInParams, "clientUuid">
  >,
) {
  const { client } = useContext(AppContext);
  return useMutation({
    ...options,
    mutationFn: (params: Omit<CheckInParams, "clientUuid">) => {
      return checkIn({
        clientUuid: client.client_uuid,
        ...params,
      });
    },
  });
}

export function useGetCalendarItemQuery(
  uuid: string,
  options: UseQueryOptions<CalendarItems>,
) {
  return useQuery({
    queryKey: ["calendar-item", uuid],
    queryFn: () => getCalendarItem(uuid),
    ...options,
  });
}

export function useGetCalendarItemsQuery(
  startTimestamp: number,
  endTimestamp: number,
) {
  return useQuery({
    queryKey: ["calendar-items", startTimestamp, endTimestamp],
    queryFn: () => getCalendarItems(startTimestamp, endTimestamp),
  });
}

export function useGetTodaysCalendarItemsQuery() {
  return useQuery({
    queryKey: ["calendar-items", "today", format(new Date(), "yyyy-MM-dd")],
    queryFn: () => getSinglePageCalendarItems(),
  });
}

export const TODAYS_CALENDAR_ITEMS_QUERY_KEY = [
  "calendar-items",
  "all-today",
  format(new Date(), "yyyy-MM-dd"),
];
export function useGetAllTodaysCalendarItemsQuery() {
  return useQuery({
    queryKey: TODAYS_CALENDAR_ITEMS_QUERY_KEY,
    queryFn: () => getAllTodaysCalendarItems(),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60,
  });
}

export function useInvitePeopleToEventMutation(
  options?: UseMutationOptions<
    { uuid: string }[],
    { message?: string },
    { userUuids: string[]; calendarItemUuid: string }
  >,
) {
  return useMutation({
    ...options,
    mutationFn: (params) => createInvitations(params),
  });
}

export function useRevokeInvitationMutation(
  options?: UseMutationOptions<void, { message: string }, string>,
) {
  return useMutation({
    ...options,
    mutationFn: (uuid) => revokeInvitation(uuid),
  });
}
